import booleanIntersects from '@turf/boolean-intersects';
import type { LineString, Point, Polygon, Position } from 'geojson';
import type { SupportedGeometry } from '../data/model';

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

const SAMPLE_DISTANCE = 3;
const MAX_POINTS = 2048;

export function appendLassoPoint(
  path: readonly ScreenPoint[],
  point: ScreenPoint,
): readonly ScreenPoint[] {
  const last = path.at(-1);
  if (last && Math.hypot(last.x - point.x, last.y - point.y) < SAMPLE_DISTANCE) return path;
  if (path.length >= MAX_POINTS) return [...path.filter((_, index) => index % 2 === 0), point];
  return [...path, point];
}

function orientation(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function crosses(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint, d: ScreenPoint): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  const onSegment = (start: ScreenPoint, end: ScreenPoint, point: ScreenPoint) =>
    point.x >= Math.min(start.x, end.x) &&
    point.x <= Math.max(start.x, end.x) &&
    point.y >= Math.min(start.y, end.y) &&
    point.y <= Math.max(start.y, end.y);
  return (
    (abC * abD < 0 && cdA * cdB < 0) ||
    (abC === 0 && onSegment(a, b, c)) ||
    (abD === 0 && onSegment(a, b, d)) ||
    (cdA === 0 && onSegment(c, d, a)) ||
    (cdB === 0 && onSegment(c, d, b))
  );
}

function openPath(path: readonly ScreenPoint[]): readonly ScreenPoint[] {
  const first = path[0];
  const last = path.at(-1);
  return first &&
    last &&
    path.length > 1 &&
    Math.hypot(first.x - last.x, first.y - last.y) < SAMPLE_DISTANCE
    ? path.slice(0, -1)
    : path;
}

/** Invalid or self-crossing freehand rings are a no-op rather than ambiguous area selection. */
export function validLassoPath(path: readonly ScreenPoint[]): boolean {
  path = openPath(path);
  if (path.length < 3) return false;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let doubleArea = 0;
  for (let index = 0; index < path.length; index += 1) {
    const point = path[index]!;
    const next = path[(index + 1) % path.length]!;
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
    doubleArea += point.x * next.y - next.x * point.y;
  }
  if (maxX - minX < 8 || maxY - minY < 8 || Math.abs(doubleArea) < 72) return false;
  for (let first = 0; first < path.length; first += 1) {
    for (let second = first + 2; second < path.length; second += 1) {
      if (first === 0 && second === path.length - 1) continue;
      if (
        crosses(
          path[first]!,
          path[(first + 1) % path.length]!,
          path[second]!,
          path[(second + 1) % path.length]!,
        )
      )
        return false;
    }
  }
  return true;
}

export function geographicLasso(
  path: readonly ScreenPoint[],
  unproject: (point: ScreenPoint) => { readonly lng: number; readonly lat: number },
): Polygon | null {
  if (!validLassoPath(path)) return null;
  const ring = openPath(path).map((point) => {
    const coordinate = unproject(point);
    return [coordinate.lng, coordinate.lat];
  });
  if (ring.some(([lng, lat]) => !Number.isFinite(lng) || !Number.isFinite(lat))) return null;
  if (ring.some(([, lat]) => Math.abs(lat!) > 90)) return null;
  // A region spanning more than half the world has ambiguous planar GeoJSON semantics.
  // Reject it safely; ordinary local antimeridian lassos use MapLibre's continuous world longitude.
  if (Math.max(...ring.map(([lng]) => lng!)) - Math.min(...ring.map(([lng]) => lng!)) > 180)
    return null;
  ring.push([...ring[0]!]);
  return { type: 'Polygon', coordinates: [ring] };
}

export function lassoIntersectsGeometry(lasso: Polygon, geometry: SupportedGeometry): boolean {
  try {
    const lassoLongitudes = lasso.coordinates[0]!.map((coordinate) => coordinate[0]!);
    const lassoMin = Math.min(...lassoLongitudes);
    const lassoMax = Math.max(...lassoLongitudes);
    const intersects = (component: Point | LineString | Polygon): boolean => {
      const lines =
        component.type === 'Point'
          ? [[component.coordinates]]
          : component.type === 'LineString'
            ? [component.coordinates]
            : component.coordinates;
      let min = Infinity;
      let max = -Infinity;
      for (const line of lines)
        for (const coordinate of line) {
          min = Math.min(min, coordinate[0]!);
          max = Math.max(max, coordinate[0]!);
        }
      const nearestShift = 360 * Math.round(((lassoMin + lassoMax) / 2 - (min + max) / 2) / 360);
      for (const shift of [nearestShift, nearestShift - 360, nearestShift + 360]) {
        if (max + shift < lassoMin || min + shift > lassoMax) continue;
        if (shift === 0) {
          if (booleanIntersects(lasso, component)) return true;
          continue;
        }
        // Translate a whole component, preserving canonical edges, area and holes. Never wrap
        // individual vertices: that would turn a wide polygon into its narrow complement.
        const position = (coordinate: Position): Position => [
          coordinate[0]! + shift,
          ...coordinate.slice(1),
        ];
        const local =
          component.type === 'Point'
            ? { ...component, coordinates: position(component.coordinates) }
            : component.type === 'LineString'
              ? { ...component, coordinates: component.coordinates.map(position) }
              : {
                  ...component,
                  coordinates: component.coordinates.map((line) => line.map(position)),
                };
        if (booleanIntersects(lasso, local)) return true;
      }
      return false;
    };
    switch (geometry.type) {
      case 'Point':
      case 'LineString':
      case 'Polygon':
        return intersects(geometry);
      case 'MultiPoint':
        return geometry.coordinates.some((coordinates) =>
          intersects({ type: 'Point', coordinates }),
        );
      case 'MultiLineString':
        return geometry.coordinates.some((coordinates) =>
          intersects({ type: 'LineString', coordinates }),
        );
      case 'MultiPolygon':
        return geometry.coordinates.some((coordinates) =>
          intersects({ type: 'Polygon', coordinates }),
        );
    }
  } catch {
    // Imported malformed geometry must not corrupt the selection gesture.
    return false;
  }
}
